import { create } from 'zustand'
import axios from 'axios'

const API_URL = 'https://www.secs.gismac.co.ke/api'

const useDataStore = create((set, get) => ({
  // Dashboard stats
  dashboardStats: null,
  isLoading: false,
  error: null,

  // Data arrays
  guards: [],
  shifts: [],
  incidents: [],
  sites: [],
  assets: [],
  audits: [],
  policies: [],
  uniforms: [],
  personnel: [],
  leaves: [],
  trainings: [],
  suppliers: [],
  budgets: [],
  expenses: [],
  invoices: [],
  receipts: [],
  quotations: [],
  offDuties: [],
  users: [],
  financialSummary: {},
  liveMonitoring: {},

  // ==========================================
  // DASHBOARD
  // ==========================================
  fetchDashboardStats: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ dashboardStats: response.data.data || {}, isLoading: false })
    } catch (error) {
      set({ dashboardStats: {}, isLoading: false, error: error.message })
    }
  },

  // ==========================================
  // GUARDS - FULL CRUD
  // ==========================================
  fetchGuards: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/guards`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ guards: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ guards: [], isLoading: false, error: error.message })
    }
  },

  createGuard: async (guardData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/guards`, guardData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ guards: [response.data.data, ...state.guards] }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  updateGuard: async (id, guardData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/guards/${id}`, guardData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        guards: state.guards.map(g => g._id === id ? response.data.data : g)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  deleteGuard: async (id) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/guards/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ guards: state.guards.filter(g => g._id !== id) }))
      return { success: true }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  fetchLiveMonitoring: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
   const response = await axios.get(`${API_URL}/dashboard/live-monitoring`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ liveMonitoring: response.data.data || {}, isLoading: false })
    } catch (error) {
      set({ liveMonitoring: {}, isLoading: false, error: error.message })
    }
  },

  // ==========================================
  // SHIFTS - FULL CRUD
  // ==========================================
  fetchShifts: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/shifts`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ shifts: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ shifts: [], isLoading: false, error: error.message })
    }
  },

  createShift: async (shiftData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/shifts`, shiftData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ shifts: [response.data.data, ...state.shifts] }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  updateShift: async (id, shiftData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/shifts/${id}`, shiftData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        shifts: state.shifts.map(s => s._id === id ? response.data.data : s)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  deleteShift: async (id) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/shifts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ shifts: state.shifts.filter(s => s._id !== id) }))
      return { success: true }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  // ==========================================
  // INCIDENTS - FULL CRUD
  // ==========================================
  fetchIncidents: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/incidents`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ incidents: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ incidents: [], isLoading: false, error: error.message })
    }
  },

  createIncident: async (incidentData) => {
    try {
      const token = localStorage.getItem('token')
      const isFormData = incidentData instanceof FormData
      const response = await axios.post(`${API_URL}/incidents`, incidentData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          ...(isFormData ? {} : { 'Content-Type': 'application/json' })
        }
      })
      set(state => ({ incidents: [response.data.data, ...state.incidents] }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  updateIncident: async (id, incidentData) => {
    try {
      const token = localStorage.getItem('token')
      const isFormData = incidentData instanceof FormData
      const response = await axios.put(`${API_URL}/incidents/${id}`, incidentData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          ...(isFormData ? {} : { 'Content-Type': 'application/json' })
        }
      })
      set(state => ({
        incidents: state.incidents.map(i => i._id === id ? response.data.data : i)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  resolveIncident: async (id, resolutionData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/incidents/${id}/resolve`, resolutionData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        incidents: state.incidents.map(i => i._id === id ? response.data.data : i)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  deleteIncident: async (id) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/incidents/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ incidents: state.incidents.filter(i => i._id !== id) }))
      return { success: true }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  // ==========================================
  // SITES - FULL CRUD
  // ==========================================
  fetchSites: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/sites`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ sites: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ sites: [], isLoading: false, error: error.message })
    }
  },

  createSite: async (siteData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/sites`, siteData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ sites: [response.data.data, ...state.sites] }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  updateSite: async (id, siteData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/sites/${id}`, siteData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        sites: state.sites.map(s => s._id === id ? response.data.data : s)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  deleteSite: async (id) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/sites/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ sites: state.sites.filter(s => s._id !== id) }))
      return { success: true }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  // ==========================================
  // ASSETS - FULL CRUD
  // ==========================================
  fetchAssets: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/assets`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ assets: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ assets: [], isLoading: false, error: error.message })
    }
  },

  createAsset: async (assetData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/assets`, assetData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ assets: [response.data.data, ...state.assets] }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },
  fetchAudits: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/audits`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ audits: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ audits: [], isLoading: false, error: error.message })
    }
  },

  fetchPolicies: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/policies`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ policies: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ policies: [], isLoading: false, error: error.message })
    }
  },

  updateAsset: async (id, assetData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/assets/${id}`, assetData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        assets: state.assets.map(a => a._id === id ? response.data.data : a)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  deleteAsset: async (id) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/assets/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ assets: state.assets.filter(a => a._id !== id) }))
      return { success: true }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  

  // ==========================================
  // UNIFORMS
  // ==========================================
  fetchUniforms: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/uniforms`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ uniforms: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ uniforms: [], isLoading: false, error: error.message })
    }
  },

  createUniform: async (uniformData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/uniforms`, uniformData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ uniforms: [response.data.data, ...state.uniforms] }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  updateUniform: async (id, uniformData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/uniforms/${id}`, uniformData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        uniforms: state.uniforms.map(u => u._id === id ? response.data.data : u)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  deleteUniform: async (id) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/uniforms/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ uniforms: state.uniforms.filter(u => u._id !== id) }))
      return { success: true }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  // ==========================================
  // PERSONNEL - FULL CRUD
  // ==========================================
  fetchPersonnel: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/personnel`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ personnel: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ personnel: [], isLoading: false, error: error.message })
    }
  },

  createPersonnel: async (personnelData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/personnel`, personnelData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ personnel: [response.data.data, ...state.personnel] }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  updatePersonnel: async (id, personnelData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/personnel/${id}`, personnelData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        personnel: state.personnel.map(p => p._id === id ? response.data.data : p)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  deletePersonnel: async (id) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/personnel/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ personnel: state.personnel.filter(p => p._id !== id) }))
      return { success: true }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  // ==========================================
  // LEAVES - FULL CRUD
  // ==========================================
  fetchLeaves: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/leave`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ leaves: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ leaves: [], isLoading: false, error: error.message })
    }
  },

  createLeave: async (leaveData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/leave`, leaveData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ leaves: [response.data.data, ...state.leaves] }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  approveLeave: async (id, remarks) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/leave/${id}/approve`, { remarks }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        leaves: state.leaves.map(l => l._id === id ? response.data.data : l)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  rejectLeave: async (id, remarks) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/leave/${id}/reject`, { remarks }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        leaves: state.leaves.map(l => l._id === id ? response.data.data : l)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  // ==========================================
  // TRAININGS - FULL CRUD
  // ==========================================
  fetchTrainings: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/training`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ trainings: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ trainings: [], isLoading: false, error: error.message })
    }
  },

  createTraining: async (trainingData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/training`, trainingData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ trainings: [response.data.data, ...state.trainings] }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  updateTraining: async (id, trainingData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/training/${id}`, trainingData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        trainings: state.trainings.map(t => t._id === id ? response.data.data : t)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  deleteTraining: async (id) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/training/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ trainings: state.trainings.filter(t => t._id !== id) }))
      return { success: true }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  // ==========================================
  // SUPPLIERS - FULL CRUD
  // ==========================================
  fetchSuppliers: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/supply-chain`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ suppliers: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ suppliers: [], isLoading: false, error: error.message })
    }
  },

  createSupplier: async (supplierData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/supply-chain`, supplierData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ suppliers: [response.data.data, ...state.suppliers] }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  updateSupplier: async (id, supplierData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/supply-chain/${id}`, supplierData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        suppliers: state.suppliers.map(s => s._id === id ? response.data.data : s)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  deleteSupplier: async (id) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/supply-chain/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ suppliers: state.suppliers.filter(s => s._id !== id) }))
      return { success: true }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  // ==========================================
  // INVOICES - FULL CRUD
  // ==========================================
  fetchInvoices: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ invoices: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ invoices: [], isLoading: false, error: error.message })
    }
  },

  createInvoice: async (invoiceData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/invoices`, invoiceData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ invoices: [response.data.data, ...state.invoices] }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  updateInvoice: async (id, invoiceData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/invoices/${id}`, invoiceData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        invoices: state.invoices.map(i => i._id === id ? response.data.data : i)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  deleteInvoice: async (id) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/invoices/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ invoices: state.invoices.filter(i => i._id !== id) }))
      return { success: true }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  recordPayment: async (id, paymentData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/invoices/${id}/payment`, paymentData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        invoices: state.invoices.map(i => i._id === id ? response.data.data : i)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  // ==========================================
  // RECEIPTS - FULL CRUD
  // ==========================================
  fetchReceipts: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/receipts`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ receipts: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ receipts: [], isLoading: false, error: error.message })
    }
  },

  createReceipt: async (receiptData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/receipts`, receiptData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ receipts: [response.data.data, ...state.receipts] }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  updateReceipt: async (id, receiptData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/receipts/${id}`, receiptData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        receipts: state.receipts.map(r => r._id === id ? response.data.data : r)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  deleteReceipt: async (id) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/receipts/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ receipts: state.receipts.filter(r => r._id !== id) }))
      return { success: true }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  // ==========================================
  // QUOTATIONS - FULL CRUD
  // ==========================================
  fetchQuotations: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/quotations`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ quotations: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ quotations: [], isLoading: false, error: error.message })
    }
  },

  createQuotation: async (quotationData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/quotations`, quotationData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ quotations: [response.data.data, ...state.quotations] }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  updateQuotation: async (id, quotationData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/quotations/${id}`, quotationData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        quotations: state.quotations.map(q => q._id === id ? response.data.data : q)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  updateQuotationStatus: async (id, status, rejectionReason) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/quotations/${id}/status`, { status, rejectionReason }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        quotations: state.quotations.map(q => q._id === id ? response.data.data : q)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  deleteQuotation: async (id) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/quotations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ quotations: state.quotations.filter(q => q._id !== id) }))
      return { success: true }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  // ==========================================
  // OFF DUTIES - FULL CRUD
  // ==========================================
  fetchOffDuties: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/offduty`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ offDuties: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ offDuties: [], isLoading: false, error: error.message })
    }
  },

  createOffDuty: async (offDutyData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/offduty`, offDutyData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ offDuties: [response.data.data, ...state.offDuties] }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  updateOffDuty: async (id, offDutyData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/offduty/${id}`, offDutyData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        offDuties: state.offDuties.map(o => o._id === id ? response.data.data : o)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  approveOffDuty: async (id, remarks) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/offduty/${id}/approve`, { remarks }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        offDuties: state.offDuties.map(o => o._id === id ? response.data.data : o)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  rejectOffDuty: async (id, remarks) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/offduty/${id}/reject`, { remarks }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        offDuties: state.offDuties.map(o => o._id === id ? response.data.data : o)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  deleteOffDuty: async (id) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/offduty/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ offDuties: state.offDuties.filter(o => o._id !== id) }))
      return { success: true }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  // ==========================================
  // BUDGETS - FULL CRUD
  // ==========================================
  fetchBudgets: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/finance/budgets`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ budgets: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ budgets: [], isLoading: false, error: error.message })
    }
  },

  createBudget: async (budgetData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/finance/budgets`, budgetData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ budgets: [response.data.data, ...state.budgets] }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  updateBudget: async (id, budgetData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/finance/budgets/${id}`, budgetData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        budgets: state.budgets.map(b => b._id === id ? response.data.data : b)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  deleteBudget: async (id) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/finance/budgets/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ budgets: state.budgets.filter(b => b._id !== id) }))
      return { success: true }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  // ==========================================
  // EXPENSES - FULL CRUD
  // ==========================================
  fetchExpenses: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/finance/expenses`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ expenses: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ expenses: [], isLoading: false, error: error.message })
    }
  },

  createExpense: async (expenseData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/finance/expenses`, expenseData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ expenses: [response.data.data, ...state.expenses] }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  updateExpense: async (id, expenseData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/finance/expenses/${id}`, expenseData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        expenses: state.expenses.map(e => e._id === id ? response.data.data : e)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  deleteExpense: async (id) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/finance/expenses/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ expenses: state.expenses.filter(e => e._id !== id) }))
      return { success: true }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  // ==========================================
  // FINANCIAL SUMMARY
  // ==========================================
  fetchFinancialSummary: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/finance/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ financialSummary: response.data.data || {}, isLoading: false })
    } catch (error) {
      set({ financialSummary: {}, isLoading: false, error: error.message })
    }
  },

  // ==========================================
  // USERS - FULL CRUD
  // ==========================================
  fetchUsers: async () => {
    set({ isLoading: true })
    try {
      const token = localStorage.getItem('token')
      const response = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set({ users: response.data.data || [], isLoading: false })
    } catch (error) {
      set({ users: [], isLoading: false, error: error.message })
    }
  },

  createUser: async (userData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.post(`${API_URL}/users`, userData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ users: [response.data.data, ...state.users] }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  updateUser: async (id, userData) => {
    try {
      const token = localStorage.getItem('token')
      const response = await axios.put(`${API_URL}/users/${id}`, userData, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({
        users: state.users.map(u => u._id === id ? response.data.data : u)
      }))
      return { success: true, data: response.data.data }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  deleteUser: async (id) => {
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      set(state => ({ users: state.users.filter(u => u._id !== id) }))
      return { success: true }
    } catch (error) {
      set({ error: error.message })
      return { success: false, error: error.message }
    }
  },

  // Clear error
  clearError: () => set({ error: null }),
  setLoading: (loading) => set({ isLoading: loading })
}))

export { useDataStore }
export default useDataStore
